/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { TankList } from '../models/tank-list';
import { TankSearchOptions } from '../models/tank-search-options';
import { Tank } from '../models/tank';
import { TankInfo } from '../models/tank-info';
import { TankLevelInfos } from '../models/tank-level-infos';
import { TankLevelUpsert } from '../models/tank-level-upsert';
import { TankConsumptionUpsert } from '../models/tank-consumption-upsert';
import { TankAverageConsumptionUpsert } from '../models/tank-average-consumption-upsert';

/**
 * All about the tanks
 */
@Injectable({
  providedIn: 'root',
})
class TanksService extends __BaseService {
  static readonly searchTanksPath = '/tanks/search';
  static readonly retrieveTankPath = '/tanks/{uid}';
  static readonly upsertTankInfoPath = '/tanks/{uid}/info/upsert';
  static readonly retrieveTankLevelPath = '/tanks/{uid}/level';
  static readonly upsertLT1ValuePath = '/tanks/{uid}/level';
  static readonly upsertConsumptionValuePath = '/tanks/{uid}/consumption';
  static readonly upsertAverageConsumptionValuePath = '/tanks/{uid}/consumption/average/upsert';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Search tanks, with pagination
   *
   * Search tanks, with pagination
   * @param params The `TanksService.SearchTanksParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the name of the tank's station
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The tank search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchTanksResponse(params: TanksService.SearchTanksParams): __Observable<__StrictHttpResponse<TankList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.sortFieldName != null) __params = __params.set('sortFieldName', params.sortFieldName.toString());
    if (params.sortDirection != null) __params = __params.set('sortDirection', params.sortDirection.toString());
    __body = params.search;
    if (params.offset != null) __params = __params.set('offset', params.offset.toString());
    if (params.limit != null) __params = __params.set('limit', params.limit.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TankList>;
      })
    );
  }
  /**
   * Search tanks, with pagination
   *
   * Search tanks, with pagination
   * @param params The `TanksService.SearchTanksParams` containing the following parameters:
   *
   * - `sortFieldName`: Indicates the field to use for sorting the data:
   *     * NAME : the name of the tank's station
   *
   * - `sortDirection`: Sort direction : ASC, DESC
   *
   * - `search`: The tank search options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  searchTanks(params: TanksService.SearchTanksParams): __Observable<TankList> {
    return this.searchTanksResponse(params).pipe(
      __map(_r => _r.body as TankList)
    );
  }

  /**
   * Retrieve a tank
   *
   * Retrieve a tank
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveTankResponse(uid: string): __Observable<__StrictHttpResponse<Tank>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Tank>;
      })
    );
  }
  /**
   * Retrieve a tank
   *
   * Retrieve a tank
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveTank(uid: string): __Observable<Tank> {
    return this.retrieveTankResponse(uid).pipe(
      __map(_r => _r.body as Tank)
    );
  }

  /**
   * Upsert a tank information note
   *
   * Upsert a tank information note
   * @param params The `TanksService.UpsertTankInfoParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tank info`: The tank information note
   *
   * @return Success
   */
  upsertTankInfoResponse(params: TanksService.UpsertTankInfoParams): __Observable<__StrictHttpResponse<TankInfo>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.tankInfo;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/info/upsert`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TankInfo>;
      })
    );
  }
  /**
   * Upsert a tank information note
   *
   * Upsert a tank information note
   * @param params The `TanksService.UpsertTankInfoParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tank info`: The tank information note
   *
   * @return Success
   */
  upsertTankInfo(params: TanksService.UpsertTankInfoParams): __Observable<TankInfo> {
    return this.upsertTankInfoResponse(params).pipe(
      __map(_r => _r.body as TankInfo)
    );
  }

  /**
   * Retrieve a tank capacity and level value for a given date
   *
   * Retrieve a tank capacity and level value for a given date
   * @param params The `TanksService.RetrieveTankLevelParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `startDate`: Start date
   *
   * @return Success
   */
  retrieveTankLevelResponse(params: TanksService.RetrieveTankLevelParams): __Observable<__StrictHttpResponse<TankLevelInfos>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (params.startDate != null) __params = __params.set('startDate', params.startDate.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/level`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TankLevelInfos>;
      })
    );
  }
  /**
   * Retrieve a tank capacity and level value for a given date
   *
   * Retrieve a tank capacity and level value for a given date
   * @param params The `TanksService.RetrieveTankLevelParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `startDate`: Start date
   *
   * @return Success
   */
  retrieveTankLevel(params: TanksService.RetrieveTankLevelParams): __Observable<TankLevelInfos> {
    return this.retrieveTankLevelResponse(params).pipe(
      __map(_r => _r.body as TankLevelInfos)
    );
  }

  /**
   * Upsert a real LT1 value
   *
   * Upsert a real LT1 value
   * @param params The `TanksService.UpsertLT1ValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankLevelUpsert`: The tank level upsert object
   */
  upsertLT1ValueResponse(params: TanksService.UpsertLT1ValueParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.tankLevelUpsert;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/level`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Upsert a real LT1 value
   *
   * Upsert a real LT1 value
   * @param params The `TanksService.UpsertLT1ValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankLevelUpsert`: The tank level upsert object
   */
  upsertLT1Value(params: TanksService.UpsertLT1ValueParams): __Observable<null> {
    return this.upsertLT1ValueResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Upsert a tank consumption value
   *
   * Upsert a tank consumption value
   * @param params The `TanksService.UpsertConsumptionValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankConsumptionUpsert`: The tank consumption upsert object
   */
  upsertConsumptionValueResponse(params: TanksService.UpsertConsumptionValueParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.tankConsumptionUpsert;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/consumption`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Upsert a tank consumption value
   *
   * Upsert a tank consumption value
   * @param params The `TanksService.UpsertConsumptionValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankConsumptionUpsert`: The tank consumption upsert object
   */
  upsertConsumptionValue(params: TanksService.UpsertConsumptionValueParams): __Observable<null> {
    return this.upsertConsumptionValueResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Upsert a tank average consumption value
   *
   * Upsert a tank average consumption value
   * @param params The `TanksService.UpsertAverageConsumptionValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankAverageConsumptionUpsert`: The tank average consumption upsert object
   */
  upsertAverageConsumptionValueResponse(params: TanksService.UpsertAverageConsumptionValueParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.tankAverageConsumptionUpsert;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/consumption/average/upsert`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Upsert a tank average consumption value
   *
   * Upsert a tank average consumption value
   * @param params The `TanksService.UpsertAverageConsumptionValueParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `tankAverageConsumptionUpsert`: The tank average consumption upsert object
   */
  upsertAverageConsumptionValue(params: TanksService.UpsertAverageConsumptionValueParams): __Observable<null> {
    return this.upsertAverageConsumptionValueResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }
}

namespace TanksService {

  /**
   * Parameters for searchTanks
   */
  export interface SearchTanksParams {

    /**
     * Indicates the field to use for sorting the data:
     *   * NAME : the name of the tank's station
     */
    sortFieldName?: 'NAME';

    /**
     * Sort direction : ASC, DESC
     */
    sortDirection?: 'ASC' | 'DESC';

    /**
     * The tank search options object
     */
    search?: TankSearchOptions;

    /**
     * The list items offset
     */
    offset?: number;

    /**
     * The list items limit
     */
    limit?: number;
  }

  /**
   * Parameters for upsertTankInfo
   */
  export interface UpsertTankInfoParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The tank information note
     */
    tankInfo: TankInfo;
  }

  /**
   * Parameters for retrieveTankLevel
   */
  export interface RetrieveTankLevelParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * Start date
     */
    startDate: string;
  }

  /**
   * Parameters for upsertLT1Value
   */
  export interface UpsertLT1ValueParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The tank level upsert object
     */
    tankLevelUpsert: TankLevelUpsert;
  }

  /**
   * Parameters for upsertConsumptionValue
   */
  export interface UpsertConsumptionValueParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The tank consumption upsert object
     */
    tankConsumptionUpsert: TankConsumptionUpsert;
  }

  /**
   * Parameters for upsertAverageConsumptionValue
   */
  export interface UpsertAverageConsumptionValueParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The tank average consumption upsert object
     */
    tankAverageConsumptionUpsert: TankAverageConsumptionUpsert;
  }
}

export { TanksService }
